import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Timer, Play, Pause, RotateCcw, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const DashboardFocus = () => {
  const [duration, setDuration] = useState(25);
  const [timeLeft, setTimeLeft] = useState(duration * 60);
  const [isRunning, setIsRunning] = useState(false);
  const [taskDescription, setTaskDescription] = useState("");
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [todaySessions, setTodaySessions] = useState(0);
  const [todayMinutes, setTodayMinutes] = useState(0);

  useEffect(() => {
    loadTodayStats();
  }, []);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (isRunning && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            handleComplete();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => clearInterval(interval);
  }, [isRunning, timeLeft]);

  const loadTodayStats = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const today = new Date().toISOString().split('T')[0];
    
    const { data, error } = await supabase
      .from("focus_sessions")
      .select("*")
      .eq("user_id", user.id)
      .gte("started_at", `${today}T00:00:00`)
      .eq("completed", true);

    if (error) {
      console.error("Error loading stats:", error);
    } else if (data) {
      setTodaySessions(data.length);
      setTodayMinutes(data.reduce((sum, session) => sum + session.duration_minutes, 0));
    }
  };

  const handleStart = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data, error } = await supabase
      .from("focus_sessions")
      .insert([{
        user_id: user.id,
        duration_minutes: duration,
        task_description: taskDescription || null,
      }])
      .select()
      .single();

    if (error) {
      toast.error("Failed to start session");
      console.error(error);
    } else {
      setSessionId(data.id);
      setIsRunning(true);
      toast.success("Focus session started!");
    }
  };

  const handlePause = () => {
    setIsRunning(false);
  };

  const handleResume = () => {
    setIsRunning(true);
  };

  const handleComplete = async () => {
    setIsRunning(false);
    
    if (sessionId) {
      const { error } = await supabase
        .from("focus_sessions")
        .update({
          completed: true,
          completed_at: new Date().toISOString(),
        })
        .eq("id", sessionId);

      if (error) {
        toast.error("Failed to save session");
      } else {
        toast.success("🎉 Focus session completed!");
        loadTodayStats();
      }
    }

    setSessionId(null);
    setTimeLeft(duration * 60);
    setTaskDescription("");
  };

  const handleReset = () => {
    setIsRunning(false);
    setTimeLeft(duration * 60);
    setSessionId(null);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <Link to="/dashboard">
        <Button variant="ghost" className="mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>
      </Link>

      <div className="flex items-center gap-3 mb-6">
        <Timer className="w-8 h-8 text-primary" />
        <h1 className="text-3xl font-bold">Focus Timer</h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Today's Sessions</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">{todaySessions}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Today's Minutes</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">{todayMinutes}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Current Streak</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">🔥 {Math.floor(todayMinutes / 25)}</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="text-center space-y-6">
            <div className="text-8xl font-bold text-primary">
              {formatTime(timeLeft)}
            </div>

            {!isRunning && timeLeft === duration * 60 && (
              <div className="space-y-4 max-w-md mx-auto">
                <div>
                  <Label htmlFor="duration">Duration (minutes)</Label>
                  <Input
                    id="duration"
                    type="number"
                    min="1"
                    max="120"
                    value={duration}
                    onChange={(e) => {
                      const newDuration = parseInt(e.target.value) || 25;
                      setDuration(newDuration);
                      setTimeLeft(newDuration * 60);
                    }}
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="task">What are you working on?</Label>
                  <Input
                    id="task"
                    value={taskDescription}
                    onChange={(e) => setTaskDescription(e.target.value)}
                    placeholder="e.g., Study Week 1 Module"
                    className="mt-2"
                  />
                </div>
              </div>
            )}

            <div className="flex gap-4 justify-center">
              {!isRunning && timeLeft === duration * 60 && (
                <Button size="lg" onClick={handleStart}>
                  <Play className="w-5 h-5 mr-2" />
                  Start Focus Session
                </Button>
              )}

              {isRunning && (
                <Button size="lg" variant="outline" onClick={handlePause}>
                  <Pause className="w-5 h-5 mr-2" />
                  Pause
                </Button>
              )}

              {!isRunning && timeLeft < duration * 60 && timeLeft > 0 && (
                <>
                  <Button size="lg" onClick={handleResume}>
                    <Play className="w-5 h-5 mr-2" />
                    Resume
                  </Button>
                  <Button size="lg" variant="outline" onClick={handleReset}>
                    <RotateCcw className="w-5 h-5 mr-2" />
                    Reset
                  </Button>
                </>
              )}

              {timeLeft === 0 && (
                <Button size="lg" onClick={handleReset}>
                  <RotateCcw className="w-5 h-5 mr-2" />
                  New Session
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardFocus;
